import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TextField, MenuItem, Select, FormControl, InputLabel, Button } from '@mui/material';
import { API_BASE_URL } from "../config/apiconfig";

const API_URL = `${API_BASE_URL}/api`;

const HireEmployees = () => {
    const { user, userType, companyId, logout } = useAuth();
    const [candidates, setCandidates] = useState([]);
    const [calledForInterviews, setCalledForInterviews] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [designations, setDesignations] = useState([]);
    const [hiredEmployees, setHiredEmployees] = useState([]);
    const [success, setSuccess] = useState(null);
    const [companyCode, setCompanyCode] = useState('');
    const [empNoLoading, setEmpNoLoading] = useState(false);
    const [empNoError, setEmpNoError] = useState(null);

    const fetchHiredEmployees = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/hired-from-vhrms`, {
                headers: {
                    'user-id': token,
                    'user-type': userType,
                    'company-id': companyId
                }
            });
            setHiredEmployees(response.data);
        } catch (err) {
            console.error('Error fetching hired employees:', err);
            setError('Failed to fetch hired employees');
        }
    };

    const fetchDesignations = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/designations-for-hiring`, {
                headers: {
                    'user-id': token,
                    'user-type': userType,
                    'company-id': companyId
                }
            });
            setDesignations(response.data);
        } catch (err) {
            console.error('Error fetching designations:', err);
            setError('Failed to fetch designations');
        }
    };

    const getLastEmpNo = async (companyId, companyCode, userId, userType) => {
        try {
            console.log('Calling last-empno endpoint with:', { companyId, prefix: companyCode });
            const response = await axios.get(`${API_URL}/employees/last-empno`, {
                headers: {
                    'user-id': userId,
                    'user-type': userType,
                    'company-id': companyId,
                },
                params: { 
                    prefix: companyCode 
                },
            });
            
            console.log('last-empno response:', response.data);
            
            if (!response.data || !response.data.lastEmpNo) {
                console.error('Invalid response format from last-empno endpoint');
                return null;
            }
            
            return response.data.lastEmpNo;
        } catch (error) {
            console.error('Error in getLastEmpNo:', error);
            if (error.response) {
                console.error('Response data:', error.response.data);
                console.error('Response status:', error.response.status);
            }
            return null;
        }
    };

    const fetchCompanyCodeAndAutoEmpNo = async () => {
        console.log('fetchCompanyCodeAndAutoEmpNo called');
        setEmpNoError(null);
        setEmpNoLoading(true);
        
        try {
            console.log('Fetching company data...');
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/companies`, {
                headers: {
                    'user-id': token,
                    'user-type': userType
                }
            });
            console.log('Companies response:', response.data);
            
            const company = response.data.find(c => String(c.id) === String(companyId));
            console.log('Found company:', company);
            
            const code = company?.company_code || '';
            setCompanyCode(code);
            
            if (code) {
                console.log('Getting last employee number for code:', code);
                const result = await getLastEmpNo(companyId, code, token, userType);
                console.log('Last employee number result:', result);
                
                if (result) {
                    console.log('Setting employee number to:', result);
                    return result;
                } else {
                    console.log('No result from getLastEmpNo');
                    return `${code}001`; // Default starting number if no employees exist
                }
            } else {
                console.log('No company code found');
                setEmpNoError('Company code not found');
                return '';
            }
        } catch (err) {
            console.error('Error in fetchCompanyCodeAndAutoEmpNo:', err);
            console.error('Error details:', err.response?.data || err.message);
            setEmpNoError('Failed to auto-generate employee number. Please enter it manually.');
            return '';
        } finally {
            console.log('Finished fetchCompanyCodeAndAutoEmpNo');
            setEmpNoLoading(false);
        }
    };

    useEffect(() => {
        if (!companyId) {
            logout();
            return;
        }
        fetchCandidates();
        fetchCalledForInterviews();
        fetchInterviewStages();
        fetchDepartments();
        fetchDesignations();
        fetchHiredEmployees();
    }, [companyId]);
    
    const [filter, setFilter] = useState({
        position: '',
        skills: '',
        location: ''
    });
    const [showHireForm, setShowHireForm] = useState(false);
    const [showScheduleForm, setShowScheduleForm] = useState(false);
    const [hireForm, setHireForm] = useState({
        candidateEmail: '',
        empNo: '',
        position: '',
        department: '',
        salary: '',
        notes: ''
    });
    const [scheduleForm, setScheduleForm] = useState({
        candidateEmail: '',
        interviewStage: '',
        scheduledDate: new Date(),
        duration: 60,
        meetingLink: '',
        notes: ''
    });
    const [departments, setDepartments] = useState([]);
    const [interviewStages, setInterviewStages] = useState([]);


    useEffect(() => {
        if (!companyId) {
            logout();
            return;
        }
        fetchCandidates();
        fetchCalledForInterviews();
        fetchInterviewStages();
        fetchDepartments();
    }, [companyId]);

    const fetchCandidates = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/candidates`, {
                headers: {
                    'user-id': token,
                    'user-type': userType
                }
            });
            
            if (!response.data) {
                throw new Error('No data received from server');
            }

            if (!Array.isArray(response.data)) {
                throw new Error('Received data is not an array');
            }

            setCandidates(response.data);
        } catch (err) {
            console.error('Fetch candidates error:', err);
            let errorMessage = 'Failed to fetch candidates';
            
            if (err.response) {
                errorMessage = err.response.data?.error || 
                              err.response.statusText || 
                              `Server error: ${err.response.status}`;
            } else if (err.request) {
                errorMessage = 'No response from server - is the backend running?';
            } else {
                errorMessage = err.message;
            }
            
            setError(errorMessage);
            setCandidates([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchCalledForInterviews = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/called-for-interviews`, {
                headers: {
                    'user-id': token,
                    'user-type': userType,
                    'company-id': companyId
                }
            });
            setCalledForInterviews(response.data);
        } catch (err) {
            console.error('Error fetching called for interviews:', err);
            setError('Failed to fetch called for interviews');
        }
    };

    const fetchInterviewStages = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/interview-stages`, {
                headers: {
                    'user-id': token,
                    'user-type': userType
                }
            });
            setInterviewStages(response.data);
        } catch (err) {
            console.error('Error fetching interview stages:', err);
            setError('Failed to fetch interview stages');
        }
    };

    const fetchDepartments = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/departments`, {
                headers: {
                    'user-id': token,
                    'user-type': userType,
                    'company-id': companyId
                }
            });
            setDepartments(response.data.map(dept => dept.dname));
        } catch (err) {
            console.error('Error fetching departments:', err);
            setError('Failed to fetch departments');
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilter(prev => ({ ...prev, [name]: value }));
    };

    // Build a quick lookup of hired emails to hide them from candidate lists
    const hiredEmailSet = new Set(
        (hiredEmployees || []).map(e => (e.email || '').toLowerCase()).filter(Boolean)
    );

    const filteredCandidates = candidates.filter(candidate => {
        const position = candidate.applyingPosition || '';
        const skills = Array.isArray(candidate.skills) ? candidate.skills : [];
        const location = candidate.location || '';
        const candEmail = (candidate.email || '').toLowerCase();
        
        return (
            !hiredEmailSet.has(candEmail) &&
            (filter.position === '' || 
             position.toLowerCase().includes(filter.position.toLowerCase())) &&
            (filter.skills === '' || 
             skills.some(skill => 
                (skill || '').toLowerCase().includes(filter.skills.toLowerCase()))) &&
            (filter.location === '' || 
             location.toLowerCase().includes(filter.location.toLowerCase()))
        );
    });

    const handleHireClick = async (candidate) => {
        try {
            setEmpNoLoading(true);
            const generatedEmpNo = await fetchCompanyCodeAndAutoEmpNo();
            
            setHireForm({
                candidateEmail: candidate.email || '',
                empNo: generatedEmpNo || `EMP-${Math.floor(1000 + Math.random() * 9000)}`,
                position: candidate.applyingPosition || '',
                department: '',
                salary: '',
                notes: ''
            });
            setShowHireForm(true);
        } catch (error) {
            console.error('Error in handleHireClick:', error);
            setError('Failed to prepare hire form. Please try again.');
        } finally {
            setEmpNoLoading(false);
        }
    };

    const handleScheduleClick = (candidate) => {
        setScheduleForm({
            candidateEmail: candidate.email || '',
            interviewStage: interviewStages.length > 0 ? interviewStages[0].id : '',
            scheduledDate: new Date(),
            duration: 60,
            meetingLink: `https://meet.google.com/new?hs=197&authuser=${user.email}`,
            notes: ''
        });
        setShowScheduleForm(true);
    };

    const handleHireFormChange = (e) => {
        const { name, value } = e.target;
        setHireForm(prev => ({ ...prev, [name]: value }));
    };

    const handleScheduleFormChange = (e) => {
        const { name, value } = e.target;
        setScheduleForm(prev => ({ ...prev, [name]: value }));
    };

    const handleHireSubmit = async (e) => {
        e.preventDefault();
        try {
            // Basic validation to avoid backend 500s due to missing data
            if (!hireForm.candidateEmail) {
                setError('Candidate email is required.');
                return;
            }
            if (!hireForm.empNo) {
                setError('Employee Number is required.');
                return;
            }
            if (!hireForm.position) {
                setError('Position is required.');
                return;
            }
            if (!hireForm.department) {
                setError('Department is required.');
                return;
            }
            const token = localStorage.getItem('token');
            await axios.post(`${API_URL}/update-candidate-status`, {
                candidateEmail: hireForm.candidateEmail,
                status: 'Hired',
                position: hireForm.position,
                department: hireForm.department,
                salary: hireForm.salary,
                empNo: hireForm.empNo, // Make sure to include empNo
                rejectionReason: ''
            }, {
                headers: {
                    'user-id': token,
                    'user-type': userType,
                    'company-id': companyId
                }
            });
            setError(null);
            setSuccess('Hired successfully. A password setup email has been sent to the employee.');
            setShowHireForm(false);
            fetchCandidates();
            fetchCalledForInterviews();
            fetchHiredEmployees(); // Add this line
        } catch (err) {
            console.error('Error hiring candidate:', err);
            if (err.response) {
                console.error('Hire error response:', err.response.data);
                setError(`Failed to hire candidate: ${err.response.data?.error || err.response.statusText || 'Server error'}`);
            } else if (err.request) {
                setError('Failed to hire candidate: No response from server');
            } else {
                setError(`Failed to hire candidate: ${err.message}`);
            }
        }
    };

    // Add function to handle deleting hired employee
    const handleDeleteHiredEmployee = async (id) => {
        if (window.confirm('Are you sure you want to delete this hired employee record?')) {
            try {
                const token = localStorage.getItem('token');
                await axios.delete(`${API_URL}/hired-from-vhrms/${id}`, {
                    headers: {
                        'user-id': token,
                        'user-type': userType
                    }
                });
                fetchHiredEmployees(); // Refresh the list
            } catch (err) {
                console.error('Error deleting hired employee:', err);
                setError('Failed to delete hired employee');
            }
        }
    };

    // Helper to format JS Date to MySQL DATETIME string
    const formatDateToMySQL = (date) => {
        const pad = (n) => String(n).padStart(2, '0');
        const year = date.getFullYear();
        const month = pad(date.getMonth() + 1);
        const day = pad(date.getDate());
        const hours = pad(date.getHours());
        const minutes = pad(date.getMinutes());
        const seconds = pad(date.getSeconds());
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    };

    const handleScheduleSubmit = async (e) => {
        e.preventDefault();
        try {
            // Basic validation
            if (!scheduleForm.candidateEmail) {
                setError('Candidate email is required to schedule an interview.');
                return;
            }
            if (!scheduleForm.interviewStage) {
                setError('Interview stage is required.');
                return;
            }
            const token = localStorage.getItem('token');
            await axios.post(`${API_URL}/schedule-interview`, {
                candidateEmail: scheduleForm.candidateEmail, // Ensure this is passed correctly
                interviewStage: scheduleForm.interviewStage,
                // MySQL DATETIME format without timezone
                scheduledDate: formatDateToMySQL(new Date(scheduleForm.scheduledDate)),
                duration: scheduleForm.duration,
                meetingLink: scheduleForm.meetingLink,
                notes: scheduleForm.notes
            }, {
                headers: {
                    'user-id': token,
                    'user-type': userType,
                    'company-id': companyId
                }
            });
            setShowScheduleForm(false);
            fetchCalledForInterviews();
        } catch (err) {
            console.error('Error scheduling interview:', err);
            if (err.response) {
                console.error('Schedule error response:', err.response.data);
                setError(`Failed to schedule interview: ${err.response.data?.error || err.response.statusText || 'Server error'}`);
            } else if (err.request) {
                setError('Failed to schedule interview: No response from server');
            } else {
                setError(`Failed to schedule interview: ${err.message}`);
            }
        }
    };

    const handleDeleteCandidate = async (email) => {
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API_URL}/called-for-interviews/${email}`, {
                headers: {
                    'user-id': token,
                    'user-type': userType,
                    'company-id': companyId
                }
            });
            fetchCalledForInterviews();
        } catch (err) {
            console.error('Error deleting candidate:', err);
            setError('Failed to delete candidate');
        }
    };

    if (!companyId) {
        return (
            <div className="w-full p-6 space-y-6 bg-gradient-to-r from-blue-900 to-blue-700 min-h-screen flex items-center justify-center">
                <div className="text-center text-lg text-white">
                    Company information not available. Please login again.
                </div>
            </div>
        );
    }

    return (
        <div className="w-full p-6 space-y-6 bg-gradient-to-r from-blue-900 to-blue-700 min-h-screen">
            <h1 className="text-4xl font-bold text-white mb-6 text-center">Hire Employees</h1>
            
            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                    {error}
                </div>
            )}
            {success && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
                    {success}
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Filter Section */}
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h2 className="text-xl font-semibold mb-4">Filter Candidates</h2>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Position</label>
                            <input
                                type="text"
                                name="position"
                                value={filter.position}
                                onChange={handleFilterChange}
                                className="w-full p-2 border rounded-md"
                                placeholder="Filter by position"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Skills</label>
                            <input
                                type="text"
                                name="skills"
                                value={filter.skills}
                                onChange={handleFilterChange}
                                className="w-full p-2 border rounded-md"
                                placeholder="Filter by skills"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                            <input
                                type="text"
                                name="location"
                                value={filter.location}
                                onChange={handleFilterChange}
                                className="w-full p-2 border rounded-md"
                                placeholder="Filter by location"
                            />
                        </div>
                    </div>
                </div>

                {/* Candidates List */}
                <div className="bg-white p-6 rounded-lg shadow-md lg:col-span-2">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-semibold">Available Candidates ({filteredCandidates.length})</h2>
                    </div>

                    {loading ? (
                        <div className="text-center py-8">Loading candidates...</div>
                    ) : filteredCandidates.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">No candidates match your filters</div>
                    ) : (
                        <div className="space-y-4 max-h-[600px] overflow-y-auto">
                            {filteredCandidates.map(candidate => (
                                <div key={candidate.email || Math.random().toString()} className="border rounded-lg p-4 hover:bg-gray-50">
                                    <div className="flex items-start space-x-4">
                                        <div className="flex-1">
                                            <div className="flex justify-between items-start">
                                                <div>
                                                    <h3 className="font-semibold text-lg">
                                                        {candidate.firstName || ''} {candidate.lastName || ''}
                                                    </h3>
                                                    <p className="text-gray-600">{candidate.location || 'No location'}</p>
                                                </div>
                                                <div className="flex space-x-2">
                                                    <button
                                                        onClick={() => handleScheduleClick(candidate)}
                                                        className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
                                                    >
                                                        Schedule
                                                    </button>
                                                    <button
                                                        onClick={() => handleHireClick(candidate)}
                                                        className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                                                    >
                                                        Hire
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="mt-2">
                                                <p className="font-medium">
                                                    Applying for: {candidate.applyingPosition || 'Not specified'}
                                                </p>
                                                <p className="text-gray-600 text-sm mt-1 line-clamp-2">
                                                    {candidate.summary || 'No summary available'}
                                                </p>
                                            </div>
                                            <div className="mt-3">
                                                <h4 className="text-sm font-medium">Skills:</h4>
                                                <div className="flex flex-wrap gap-2 mt-1">
                                                    {Array.isArray(candidate.skills) ? candidate.skills.map((skill, index) => (
                                                        <span 
                                                            key={index} 
                                                            className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded"
                                                        >
                                                            {skill || 'Unnamed skill'}
                                                        </span>
                                                    )) : (
                                                        <span className="text-gray-500 text-xs">No skills listed</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Called for Interviews Section */}
            <div className="mt-8 bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-xl font-semibold mb-4">Called for Interviews</h2>
                {(() => {
                    const filteredCalledForInterviews = (calledForInterviews || []).filter(c => {
                        const email = (c.email || '').toLowerCase();
                        const status = (c.status || '').toLowerCase();
                        return status !== 'hired' && !hiredEmailSet.has(email);
                    });
                    return filteredCalledForInterviews.length === 0 ? (
                    <p className="text-gray-500">No candidates called for interviews yet</p>
                    ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Candidate</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Interview Date</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Meeting Link</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredCalledForInterviews.map(candidate => (
                                    <tr key={candidate.email}>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="ml-4">
                                                    <div className="text-sm font-medium text-gray-900">
                                                        {candidate.firstName || ''} {candidate.lastName || ''}
                                                    </div>
                                                    <div className="text-sm text-gray-500">{candidate.email}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {candidate.interview_date ? new Date(candidate.interview_date).toLocaleString() : 'Not scheduled'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                candidate.status === 'Scheduled' ? 'bg-blue-100 text-blue-800' :
                                                candidate.status === 'Hired' ? 'bg-green-100 text-green-800' :
                                                candidate.status === 'Rejected' ? 'bg-red-100 text-red-800' :
                                                'bg-gray-100 text-gray-800'
                                            }`}>
                                                {candidate.status || 'Unknown'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {candidate.meeting_link ? (
                                                <a href={candidate.meeting_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                                    Join Meeting
                                                </a>
                                            ) : 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <button
                                                onClick={() => handleDeleteCandidate(candidate.email)}
                                                className="text-red-600 hover:text-red-900 mr-4"
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    );
                })()}
            </div>

            {/* NEW: Hired Employees Section - Add this after the Called for Interviews section */}
            <div className="mt-8 bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-xl font-semibold mb-4">Employees Hired Through VHRMS</h2>
                {hiredEmployees.length === 0 ? (
                    <p className="text-gray-500">No employees hired through VHRMS yet</p>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee ID</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Position</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Department</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hire Date</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Salary</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hired By</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {hiredEmployees.map(employee => (
                                    <tr key={employee.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900">
                                                {employee.empNo}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900">
                                                {employee.name}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">{employee.email}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">{employee.mobile || 'N/A'}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">{employee.designation}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">{employee.department_name}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">
                                                {new Date(employee.hire_date).toLocaleDateString()}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">
                                                ${employee.salary ? employee.salary.toLocaleString() : 'N/A'}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">{employee.hired_by}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            <button
                                                onClick={() => handleDeleteHiredEmployee(employee.id)}
                                                className="text-red-600 hover:text-red-900 mr-4"
                                                title="Delete record"
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Hire Candidate Modal */}
            {showHireForm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-semibold">Hire Candidate</h3>
                                <button 
                                    onClick={() => setShowHireForm(false)}
                                    className="text-gray-500 hover:text-gray-700"
                                >
                                    ✕
                                </button>
                            </div>
                            <form onSubmit={handleHireSubmit}>
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Employee ID</label>
                                        <input
                                            type="text"
                                            name="empNo"
                                            value={hireForm.empNo}
                                            onChange={handleHireFormChange}
                                            className="w-full p-2 border rounded-md"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Position</label>
                                        <select
                                            name="position"
                                            value={hireForm.position}
                                            onChange={handleHireFormChange}
                                            className="w-full p-2 border rounded-md"
                                            required
                                        >
                                            <option value="">Select Position</option>
                                            {designations.map(designation => (
                                                <option key={designation.id} value={designation.name}>
                                                    {designation.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                                        <select
                                            name="department"
                                            value={hireForm.department}
                                            onChange={handleHireFormChange}
                                            className="w-full p-2 border rounded-md"
                                            required
                                        >
                                            <option value="">Select Department</option>
                                            {departments.map(dept => (
                                                <option key={dept} value={dept}>{dept}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Salary</label>
                                        <input
                                            type="number"
                                            name="salary"
                                            value={hireForm.salary}
                                            onChange={handleHireFormChange}
                                            className="w-full p-2 border rounded-md"
                                            required
                                        />
                                    </div>
                                </div>
                                <div className="mt-6 flex justify-end space-x-3">
                                    <button
                                        type="button"
                                        onClick={() => setShowHireForm(false)}
                                        className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-100"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                    >
                                        Confirm Hire
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Schedule Interview Modal */}
            {showScheduleForm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                        <div className="p-6">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-semibold">Schedule Interview</h3>
                                <button 
                                    onClick={() => setShowScheduleForm(false)}
                                    className="text-gray-500 hover:text-gray-700"
                                >
                                    ✕
                                </button>
                            </div>
                            <form onSubmit={handleScheduleSubmit}>
                                <div className="space-y-4">
                                    <FormControl fullWidth>
                                        <InputLabel>Interview Stage</InputLabel>
                                        <Select
                                            value={scheduleForm.interviewStage}
                                            onChange={(e) => setScheduleForm({...scheduleForm, interviewStage: e.target.value})}
                                            required
                                        >
                                            {interviewStages.map(stage => (
                                                <MenuItem key={stage.id} value={stage.id}>
                                                    {stage.stage_name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    
                                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <DateTimePicker
                                            label="Interview Date & Time"
                                            value={scheduleForm.scheduledDate}
                                            onChange={(newValue) => setScheduleForm({...scheduleForm, scheduledDate: newValue})}
                                            renderInput={(params) => <TextField {...params} fullWidth />}
                                        />
                                    </LocalizationProvider>
                                    
                                    <TextField
                                        label="Duration (minutes)"
                                        type="number"
                                        value={scheduleForm.duration}
                                        onChange={(e) => setScheduleForm({...scheduleForm, duration: e.target.value})}
                                        fullWidth
                                    />
                                    
                                    <TextField
                                        label="Meeting Link"
                                        value={scheduleForm.meetingLink}
                                        onChange={(e) => setScheduleForm({...scheduleForm, meetingLink: e.target.value})}
                                        fullWidth
                                    />
                                    
                                    <TextField
                                        label="Notes"
                                        multiline
                                        rows={3}
                                        value={scheduleForm.notes}
                                        onChange={(e) => setScheduleForm({...scheduleForm, notes: e.target.value})}
                                        fullWidth
                                    />
                                </div>
                                <div className="mt-6 flex justify-end space-x-3">
                                    <Button
                                        variant="outlined"
                                        onClick={() => setShowScheduleForm(false)}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                    >
                                        Schedule
                                    </Button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default HireEmployees;